// Package imports:
import React, { useMemo, useRef, useState } from "react";
// Component imports:
import Button from "../../ui-elements/Button/Button";
import Link from "../../ui-elements/Link/Link";
import Loading from "../../ui-elements/Loading/Loading";
import DisplayBox from "../../ui-elements/DisplayBox/DisplayBox";
import Table from "../../ui-elements/Table/Table";
import SearchResults from "../../ui-elements/SearchResults/SearchResults";
// Type imports:
import { Subscription } from "../../types/KeldanTypes";
//Constants imports:
import ForeignPepSearchCompany from "./ForeignSearchCompanyForm";
import { ForeignCompanyPep, PepCompanyResult } from "../../types/ForeignCompanyPepTypes";
import ForeignCompanyPepModal from "./ForeignCompanyPepModal";
import { formatCategories } from "./PepPage";
import { isSubscriptionValid } from "../../services/utils";

interface IProps {
  isAuthenticated: boolean;
  subscriptions: Subscription[] | null;
}

export interface IForeignSearchParams {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  dob?: string;
}
const ForeignSearchCompany: React.FC<IProps> = ({ isAuthenticated, subscriptions }) => {
  //modals
  const [companyShowModal, setCompanyShowModal] = useState(false);
  const [companyModalData, setCompanyModalData] = useState<PepCompanyResult | undefined>(undefined);
  //type to know which search results to display
  const [companyData, setCompanyData] = useState<ForeignCompanyPep | null | undefined>(undefined);

  const searchResultsRef = useRef<HTMLDivElement>(null);
  const cleanResultsAndForm = () => {
    setCompanyData(undefined);
  };

  const displayResultSection = () => {
    if (companyData === undefined) return <></>;
    return (
      <SearchResults
        title={"Niðurstöður"}
        show
        closeResults={() => cleanResultsAndForm()}
        ref={searchResultsRef}
        component={
          companyData === null ? (
            <DisplayBox>
              <Loading />
            </DisplayBox>
          ) : (
            <DisplayBox
              asideComponent={
                <Link
                  targetBlank
                  linkSize="15"
                  url={`/Pdf/Althjodlegir-valistar-logadilar/${companyData.reportID}`}
                  icon="pdf"
                >
                  Sækja skýrslu
                </Link>
              }
            >
              {tableComponent}
            </DisplayBox>
          )
        }
      />
    );
  };

  const tableComponent = useMemo(() => {
    return (
      <Table
        tableSize="lg"
        data={companyData?.match ?? []}
        showEmptyTableText
        columns={[
          {
            title: "Match Rate",
            renderCell: ({ match_rate, matchRate }) => match_rate ?? matchRate,
            textAlign: "left"
          },
          {
            title: "Nafn",
            renderCell: ({ companyName }) => companyName ?? "",
            textAlign: "left",
          },
          {
            title: "Land",
            renderCell: ({ locations }) => {
              if (!locations || locations.length === 0) return "-";

              const codes = new Set<string>();
              for (const l of locations) {
                const code = l?.countryCode ?? "";
                if (code.length > 0)codes.add(code);
              }

              return Array.from(codes).join(", ");
            },
            textAlign: "left",
          },
          {
            title: "Lýsing",
            renderCell: ({ generalInfo }) => generalInfo?.businessDescription,
            textAlign: "left",
          },
          {
            title: "Flokkur/ar",
            renderCell: ({ categories }) => formatCategories(categories),
            textAlign: "left",
          },
          {
            title: "Ítarefni",
            renderCell: (item) => (
              <Button
                onClick={() => {
                  setCompanyModalData(item);
                  setCompanyShowModal(true);
                }}
                buttonType="secondary"
                size="sm"
              >
                Skoða
              </Button>
            ),
          },
        ]}
      />
    );
  }, [companyData]);

  const handleSearchResults = (data: ForeignCompanyPep | null | undefined) => {
    setCompanyData(data);
  };

  const isDisabled = useMemo(() => {
    return !isSubscriptionValid(subscriptions, isAuthenticated, 29);
  }, [isAuthenticated, subscriptions]);
  return (
    <>
      <div className="KCL_pep-search shell">
        <div className="main__inner-fluid">
          <div className="descriptionWrapper">
            <p className="summary-paragraph">
              Hér geta tilkynningarskyldir aðilar kannað stöðu lögaðila gagnvart alþjóðlegum þvingunarlistum og
              öðrum válistum. Leitað er eftir landi og nafni.
            </p>
            <p className="paragraph">
              Sbr. lög nr. 140/2018 um peningaþvætti og lög nr. 68/2023 um frystingu fjármuna og um framkvæmd
              alþjóðlegra þvingunaraðgerða, ber tilkynningarskyldum aðilum að kanna hvort viðskiptavinir séu á slíkum alþjóðlegum válistum. 
            </p>
            <p className="paragraph--bold">
              Athugið að hver uppfletting er samkvæmt{" "}
              <Link linkSize="18" url="/Verdskra#Gagnaleit">
                verðskrá Keldunnar
              </Link>
            </p>
          </div>
          <ForeignPepSearchCompany
            isDisabled={isDisabled}
            changeSearchResults={handleSearchResults}
            isAuthenticated={isAuthenticated}
          />
        </div>
        <div>
          {isDisabled && (
            <div style={{ marginTop: "30px" }}>
              <p className="paragraph">
                Ertu tilkynningarskyldur aðili en ekki með aðgang?{" "}
                <Link url="/Hafa-Samband/PEP" linkSize="18">
                  Hafðu samband.
                </Link>
              </p>
            </div>
          )}
          {isAuthenticated && (
            <div style={{ marginTop: "50px" }}>
              <Link url="/Min-Kelda/Keyptar-Skyrslur/Althjodlegir-Valistar-Logadilar" linkSize="16" icon="forward">
                Sjá allar uppflettingar
              </Link>
            </div>
          )}
        </div>
      </div>
      {displayResultSection()}
      <ForeignCompanyPepModal
        show={companyShowModal}
        close={() => setCompanyShowModal(false)}
        data={companyModalData}
      />
    </>
  );
};

export default ForeignSearchCompany;
